/**
 * useDraftSave Hook
 * 
 * Provides draft save functionality with React Query mutation.
 * Handles serialization and API call without validation (for autosave).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { telemetryService } from '@/services/telemetryService';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useDraftModeStore } from '@/stores/draftModeStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { saveDraftWorkflow } from '@/services/draftWorkflowApi';
import { applicationKeys } from './useApplicationManagement';
import { buildDraftSavePayload } from '@/services/workflowSerializer';
import { getBlockingCodeIssue } from './useIsDraftDirty';
import { saveNodesMetadata } from '@/services/nodesMetadataApi';
import { commitSidecarPlan, planDraftSidecar } from '@/services/nodesMetadataCache';
import { saveMockData } from '@/services/mockDataApi';
import { commitMockPlan, planMockData } from '@/services/mockDataCache';
import type { DraftSaveResult } from '@/types/draftMode';

export interface UseDraftSaveOptions {
  /** Workflow name to save */
  workflowName: string;
  /** Callback on successful save */
  onSuccess?: () => void;
  /** Callback on save error */
  onError?: (error: Error) => void;
}

export interface UseDraftSaveResult {
  /** Trigger the draft save operation (skips validation) */
  save: () => Promise<DraftSaveResult>;
  /** Whether a save is in progress */
  isSaving: boolean;
  /** Last save time */
  lastSavedTime: Date | null;
  /** Last save error, if any */
  error: string | null;
}

/**
 * Hook to save the current workflow as a draft (skips validation)
 */
export function useDraftSave({
  workflowName,
  onSuccess,
  onError,
}: UseDraftSaveOptions): UseDraftSaveResult {
  const { getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();
  const queryClient = useQueryClient();

  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get workflow state (used for guard check in save callback; mutation reads fresh state)
  const workflow = useWorkflowStore((state) => state.workflow);
  const markWorkflowClean = useWorkflowStore((state) => state.markClean);

  // Get draft mode store actions to update draft artifacts
  const setDraftArtifacts = useDraftModeStore((state) => state.setDraftArtifacts);

  // Mirror the per-save status into the draft mode store so consumers that
  // can't see this hook's local React state (e.g. `AutosaveStatus` rendered
  // above `DraftModeProvider`) still reflect the result of a manual save.
  // `useAutosave` ALSO writes these in its own onSuccess callback — that's a
  // harmless duplicate write, but ensures every code path (autosave, manual
  // Save button, save-before-run) keeps the store in sync.
  const setStoreDraftStatus = useDraftModeStore((state) => state.setDraftStatus);
  const setStoreLastSavedTime = useDraftModeStore((state) => state.setLastSavedTime);
  const setStoreSaveError = useDraftModeStore((state) => state.setSaveError);

  // Get connections store — we both export its contents into the save and,
  // on success, must clear its dirty flag (draft saves persist BOTH stores;
  // leaving connections dirty would keep the Save-draft button enabled
  // forever even though there's nothing left to persist).
  const exportConnections = useConnectionStore((state) => state.exportToConnectionsConfig);
  const markConnectionsClean = useConnectionStore((state) => state.markClean);

  // Extended result type to pass saved artifacts to onSuccess
  type DraftSaveResultExtended = DraftSaveResult & {
    notes?: Record<string, unknown> | null;
    savedWorkflow?: typeof workflow;
    savedConnections?: import('@/types/logicApps').ConnectionsConfiguration | null;
  };

  // Per-mutation context attached by `onMutate` and consumed by
  // `onSuccess` / `onError`. Captured PER CALL so concurrent invocations
  // (e.g. user mashes Ctrl/Cmd+S, or an autosave tick races a manual save)
  // each get their own snapshot — a shared ref would let the later call
  // overwrite the earlier one's data and silently mark stores clean for
  // edits that hadn't been persisted yet.
  type SaveContext = {
    startTimeMs: number;
    changeCount: { workflow: number; connections: number };
  };

  // Mutation for saving draft workflow
  const mutation = useMutation<DraftSaveResultExtended, Error, void, SaveContext>({
    onMutate: (): SaveContext => {
      setStoreSaveError(null);
      setError(null);
      setStoreDraftStatus('saving');
      return {
        startTimeMs: performance.now(),
        // Snapshot store changeCounts BEFORE the network call so `onSuccess`
        // can detect edits that landed during the in-flight save and avoid
        // marking the affected store clean.
        changeCount: {
          workflow: useWorkflowStore.getState().changeCount,
          connections: useConnectionStore.getState().changeCount,
        },
      };
    },
    mutationFn: async (): Promise<DraftSaveResultExtended> => {
      // Read workflow and connections from the store at execution time (not from
      // the React hook closure) so that synchronous mutations made just before
      // save — e.g. by applyTemplateModeSelections() — are picked up.
      const currentWorkflow = useWorkflowStore.getState().workflow;
      const currentConnections = useWorkflowStore.getState().connections;

      const blockingCodeIssue = getBlockingCodeIssue();
      if (blockingCodeIssue) {
        telemetryService.trackEvent('workflow.draft.save_blocked', {
          reason: blockingCodeIssue.reason,
        });
        throw new Error(blockingCodeIssue.message);
      }

      if (!currentWorkflow) {
        throw new Error('No workflow to save');
      }

      if (!selectedApp || !selectedProject) {
        throw new Error('No environment or app selected');
      }

      // Build the API payload via the shared helper so every draft save
      // path (autosave, manual save, save-and-switch) cleans + normalizes
      // empty configs the same way. Pass the freshly-read `currentWorkflow`
      // snapshot (not the React closure `workflow`) so synchronous mutations
      // made just before save — e.g. by `applyTemplateModeSelections()` —
      // are picked up, and include `draftParameters` from the draft mode
      // store so template-applied parameters are persisted alongside.
      const draftParameters = useDraftModeStore.getState().draftParameters;
      const payload = buildDraftSavePayload(
        currentWorkflow,
        exportConnections(),
        draftParameters,
      );

      // Build the sidecar BEFORE firing network requests so we can run the
      // sidecar PUT in parallel with the workflow PUT. `previous` comes from
      // a process-local cache seeded at workflow load — saves no longer need
      // a pre-save GET to preserve the other-mode branch. The sidecar is
      // best-effort UX; the workflow itself is the contract.
      const identity = useWorkflowStore.getState().currentWorkflowIdentity;
      const sidecarPlan = planDraftSidecar(currentWorkflow, identity);
      const sidecarCtx = {
        projectName: selectedProject,
        appName: selectedApp.name,
        getToken: getOttoToken,
        workflowName,
      };

      // Draft-only mock sidecar — persisted alongside the workflow so mock
      // edits (which mark the workflow dirty) survive a reload. Best-effort.
      const mockPlan = planMockData(
        useWorkflowStore.getState().mockedNodes,
        useWorkflowStore.getState().mockRunEnabled ?? false,
        identity,
      );

      const [result, sidecarOk, mockOk] = await Promise.all([
        saveDraftWorkflow({
          ...sidecarCtx,
          ...payload,
          skipValidation: true, // Always skip validation for draft saves
        }),
        sidecarPlan ? saveNodesMetadata(sidecarCtx, sidecarPlan.file) : Promise.resolve(false),
        mockPlan ? saveMockData(sidecarCtx, mockPlan.file) : Promise.resolve(false),
      ]);

      if (!result.success) {
        throw new Error(result.error || 'Failed to save draft');
      }
      if (sidecarPlan && sidecarOk) {
        commitSidecarPlan(sidecarPlan);
      }
      if (mockPlan && mockOk) {
        commitMockPlan(mockPlan);
      }

      return {
        success: true,
        savedAt: new Date(),
        notes: payload.notesConfig ?? null,
        savedWorkflow: currentWorkflow,
        savedConnections: currentConnections,
      };
    },
    onSuccess: (result, _vars, context) => {
      const duration_ms = performance.now() - context.startTimeMs;

      setLastSavedTime(result.savedAt ?? new Date());
      setError(null);

      // Update draft mode store with the saved workflow, connections, parameters, and notes.
      // Use the workflow/connections snapshots captured at mutation time (not the
      // React hook values) to ensure patched data is reflected.
      if (result.savedWorkflow) {
        const savedParameters = useDraftModeStore.getState().draftParameters;
        setDraftArtifacts({
          workflow: result.savedWorkflow,
          connections: result.savedConnections ?? null,
          parameters: savedParameters,
          notes: result.notes,
        });
      }

      // Mark each store clean ONLY if its changeCount hasn't advanced during
      // this save — otherwise a fresh edit would be silently dropped.
      if (useWorkflowStore.getState().changeCount === context.changeCount.workflow) {
        markWorkflowClean();
      }
      if (useConnectionStore.getState().changeCount === context.changeCount.connections) {
        markConnectionsClean();
      }

      // Sync the draft mode store so the sub-header status pill flips out of
      // its "dirty" state immediately on manual saves too (the autosave hook
      // does the same write, but only when autosave is enabled).
      setStoreLastSavedTime(result.savedAt ?? new Date());
      setStoreSaveError(null);
      setStoreDraftStatus('saved');

      telemetryService.trackEvent('workflow.draft.saved', undefined, { duration_ms });

      // Invalidate revisions so the preparing-revision gate picks up any
      // container restart triggered by the draft save.
      const { selectedProject: project, selectedApp: app } = useAppStore.getState();
      if (project && app) {
        queryClient.invalidateQueries({
          queryKey: applicationKeys.revisions(project, app.name),
        });
      }

      onSuccess?.();
    },
    onError: (err: Error) => {
      console.error('[useDraftSave] Save failed:', err);
      setError(err.message);
      // Surface the failure on the shared store as well so the status pill
      // can render an "Error saving" badge regardless of which save path
      // triggered the write. Flipping status to 'error' also clears the
      // 'saving' flag set in `onMutate` so spinners stop.
      setStoreSaveError(err.message);
      setStoreDraftStatus('error');
      telemetryService.trackEvent('workflow.draft.save_failed', { error: err.message });
      onError?.(err);
    },
  });

  const save = useCallback(async (): Promise<DraftSaveResult> => {
    if (!workflow || !selectedApp || !selectedProject) {
      return {
        success: false,
        error: 'No workflow or app selected',
      };
    }

    try {
      return await mutation.mutateAsync();
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }, [workflow, selectedApp, selectedProject, mutation]);

  return {
    save,
    isSaving: mutation.isPending,
    lastSavedTime,
    error,
  };
}
